import {
  buildPayload,
  getFieldValueMode,
  isButtonFieldType,
  isNumericFieldType,
  validateDraft,
} from './configurationAdminUtils';

export const FIELD_VALUE_BULK_HEADERS = [
  'Id',
  'ConfigFieldId',
  'ValueMode',
  'OptionValue',
  'MinValue',
  'MaxValue',
  'MaxRows',
  'IsDefaultValue',
];

const asText = (value) => String(value ?? '').trim();

const normalizeHeader = (value) => asText(value).toLowerCase().replace(/[^a-z0-9]/g, '');

const HEADER_BY_KEY = new Map(FIELD_VALUE_BULK_HEADERS.map((header) => [normalizeHeader(header), header]));
// Continue accepting older spreadsheets while exporting the API's current name.
HEADER_BY_KEY.set(normalizeHeader('MaxClicks'), 'MaxRows');

let bulkRowKey = 0;

const nextClientKey = () => `field-value-bulk-${++bulkRowKey}`;

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 1) return true;
  if (value === 0 || value === null || value === undefined || value === '') return false;
  const normalized = asText(value).toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
  if (['false', 'no', 'n', '0'].includes(normalized)) return false;
  return value;
};

const isBlankMatrixRow = (row = []) => row.every((value) => asText(value) === '');

const getRangeOptionValue = (minValue, maxValue) => {
  if (asText(minValue) === '' || asText(maxValue) === '') return '';
  return `${asText(minValue)}-${asText(maxValue)}`;
};

export const createBlankFieldValueBulkRow = (field) => ({
  clientKey: nextClientKey(),
  id: '',
  configFieldId: field.id,
  valueMode: 'fixed',
  optionValue: '',
  minValue: '',
  maxValue: '',
  maxClicks: '',
  isDefaultValue: false,
  isExisting: false,
});

export const createFieldValueBulkRows = (values = [], field) => values.map((value) => ({
  clientKey: nextClientKey(),
  id: value.id,
  configFieldId: value.configFieldId,
  valueMode: getFieldValueMode(value),
  optionValue: value.optionValue,
  minValue: value.minValue ?? '',
  maxValue: value.maxValue ?? '',
  maxClicks: value.maxClicks ?? '',
  isDefaultValue: Boolean(value.isDefaultValue),
  isExisting: true,
}));

export const parseFieldValueImportMatrix = (matrix = [], field) => {
  const firstPopulatedRowIndex = matrix.findIndex((row) => !isBlankMatrixRow(row));
  if (firstPopulatedRowIndex < 0) throw new Error('The uploaded table is empty.');

  const rawHeaders = matrix[firstPopulatedRowIndex];
  const mappedHeaders = rawHeaders.map((header) => HEADER_BY_KEY.get(normalizeHeader(header)) || '');
  const unknownHeaders = rawHeaders.filter((header, index) => asText(header) && !mappedHeaders[index]);
  if (unknownHeaders.length > 0) {
    throw new Error(`Unknown column${unknownHeaders.length === 1 ? '' : 's'}: ${unknownHeaders.join(', ')}.`);
  }
  if (isButtonFieldType(field.fieldType)) {
    if (!mappedHeaders.includes('MaxRows')) throw new Error('The table must include a MaxRows column.');
  } else if (!mappedHeaders.includes('OptionValue')) {
    throw new Error('The table must include an OptionValue column.');
  }

  const records = matrix
    .slice(firstPopulatedRowIndex + 1)
    .filter((row) => !isBlankMatrixRow(row))
    .map((row) => Object.fromEntries(mappedHeaders
      .map((header, index) => [header, row[index]])
      .filter(([header]) => header)));

  if (records.length === 0) throw new Error('The table does not contain any value rows.');

  return records.map((record) => {
    const valueMode = isNumericFieldType(field.fieldType)
      ? (asText(record.ValueMode).toLowerCase() || 'fixed')
      : 'fixed';
    const minValue = record.MinValue ?? '';
    const maxValue = record.MaxValue ?? '';
    return {
      clientKey: nextClientKey(),
      id: asText(record.Id),
      configFieldId: asText(record.ConfigFieldId) === '' ? field.id : Number(record.ConfigFieldId),
      valueMode,
      optionValue: valueMode === 'range'
        ? getRangeOptionValue(minValue, maxValue)
        : asText(record.OptionValue),
      minValue,
      maxValue,
      maxClicks: record.MaxRows ?? '',
      isDefaultValue: parseBoolean(record.IsDefaultValue),
      isExisting: false,
    };
  });
};

export const mergeImportedFieldValueRows = (currentRows = [], importedRows = []) => {
  const importedIds = new Set();
  importedRows.forEach((row) => {
    if (!asText(row.id)) return;
    const id = Number(row.id);
    if (importedIds.has(id)) throw new Error(`The import contains duplicate ID ${id}.`);
    importedIds.add(id);
  });

  const nextRows = [...currentRows];
  importedRows.forEach((row) => {
    const numericId = Number(row.id);
    const existingIndex = row.id === '' ? -1 : nextRows.findIndex((candidate) => Number(candidate.id) === numericId);
    if (existingIndex >= 0) {
      nextRows[existingIndex] = {
        ...row,
        clientKey: nextRows[existingIndex].clientKey,
        isExisting: true,
      };
    } else {
      nextRows.push(row);
    }
  });
  return nextRows;
};

const getComparablePayload = (row, field, mode) => buildPayload('fieldValues', {
  ...row,
  configFieldId: field.id,
  fieldType: field.fieldType,
}, mode);

const getValueIdentity = (row, field) => {
  if (isButtonFieldType(field.fieldType)) return `button:${Number(row.maxClicks)}`;
  if (isNumericFieldType(field.fieldType)) {
    if (row.valueMode === 'range') return `range:${Number(row.minValue)}:${Number(row.maxValue)}`;
    return `fixed-number:${Number(row.optionValue)}`;
  }
  return `fixed:${asText(row.optionValue).toLocaleLowerCase()}`;
};

const payloadsMatch = (left, right) => (
  Number(left.ConfigFieldId) === Number(right.ConfigFieldId)
  && asText(left.OptionValue) === asText(right.OptionValue)
  && Boolean(left.IsDefaultValue) === Boolean(right.IsDefaultValue)
  && (left.MinValue ?? null) === (right.MinValue ?? null)
  && (left.MaxValue ?? null) === (right.MaxValue ?? null)
  && (left.MaxRows ?? null) === (right.MaxRows ?? null)
);

export const analyzeFieldValueBulkRows = (rows = [], baselineValues = [], field) => {
  const baselineById = new Map(baselineValues.map((value) => [Number(value.id), value]));
  const errorsByKey = {};
  const identities = new Map();
  const ids = new Map();

  rows.forEach((row, index) => {
    const errors = [];
    const numericId = Number(row.id);
    const hasId = asText(row.id) !== '';
    if (Number(row.configFieldId) !== Number(field.id)) errors.push(`ConfigFieldId must be ${field.id}.`);
    if (hasId && (!Number.isInteger(numericId) || numericId <= 0)) errors.push('ID must be a positive whole number or blank for a new value.');
    if (hasId && !baselineById.has(numericId)) errors.push(`ID ${row.id} is not an existing value for this field.`);
    if (hasId && ids.has(numericId)) errors.push(`ID ${row.id} is repeated in rows ${ids.get(numericId)} and ${index + 1}.`);
    if (hasId) ids.set(numericId, index + 1);

    if (typeof row.isDefaultValue !== 'boolean') errors.push('IsDefaultValue must be TRUE or FALSE.');

    const normalizedMode = asText(row.valueMode).toLowerCase();
    if (isNumericFieldType(field.fieldType)) {
      if (!['fixed', 'range'].includes(normalizedMode)) errors.push('ValueMode must be Fixed or Range.');
      if (normalizedMode === 'fixed' && (asText(row.minValue) || asText(row.maxValue))) {
        errors.push('Fixed values cannot contain MinValue or MaxValue.');
      }
      if (normalizedMode === 'range' && row.isDefaultValue === true) errors.push('A range cannot be the general default.');
    } else {
      if (normalizedMode !== 'fixed') errors.push(`${field.fieldType} values must use Fixed mode.`);
      if (asText(row.minValue) || asText(row.maxValue)) errors.push(`${field.fieldType} values cannot contain MinValue or MaxValue.`);
    }

    const draftError = validateDraft('fieldValues', {
      ...row,
      fieldType: field.fieldType,
      valueMode: normalizedMode,
    });
    if (draftError) errors.push(draftError);

    if (!draftError) {
      const identity = getValueIdentity({ ...row, valueMode: normalizedMode }, field);
      if (identities.has(identity)) {
        errors.push(`Duplicates row ${identities.get(identity)}.`);
      } else {
        identities.set(identity, index + 1);
      }
    }

    if (errors.length > 0) errorsByKey[row.clientKey] = errors;
  });

  const defaultRows = rows.filter((row) => row.isDefaultValue === true);
  if (defaultRows.length > 1) {
    defaultRows.forEach((row) => {
      errorsByKey[row.clientKey] = [...(errorsByKey[row.clientKey] || []), 'Only one general default is allowed for this field.'];
    });
  }

  const operations = [];
  let unchangedCount = 0;
  rows.forEach((row) => {
    if (errorsByKey[row.clientKey]) return;
    const hasId = asText(row.id) !== '';
    const payload = getComparablePayload({ ...row, valueMode: asText(row.valueMode).toLowerCase() }, field, hasId ? 'edit' : 'create');
    if (!hasId) {
      operations.push({ method: 'POST', payload, clientKey: row.clientKey });
      return;
    }
    const baseline = baselineById.get(Number(row.id));
    const baselinePayload = getComparablePayload({
      ...baseline,
      valueMode: getFieldValueMode(baseline),
    }, field, 'edit');
    if (payloadsMatch(payload, baselinePayload)) unchangedCount += 1;
    else operations.push({ method: 'PUT', payload, clientKey: row.clientKey });
  });

  return {
    errorsByKey,
    errorCount: Object.values(errorsByKey).reduce((total, errors) => total + errors.length, 0),
    operations,
    createCount: operations.filter((operation) => operation.method === 'POST').length,
    updateCount: operations.filter((operation) => operation.method === 'PUT').length,
    unchangedCount,
  };
};

const comparableServerValue = (value) => ({
  id: Number(value.id),
  configFieldId: Number(value.configFieldId),
  optionValue: asText(value.optionValue),
  isDefaultValue: Boolean(value.isDefaultValue),
  minValue: value.minValue ?? null,
  maxValue: value.maxValue ?? null,
  maxClicks: value.maxClicks ?? null,
});

export const getFieldValueSnapshotConflict = (baselineValues = [], currentValues = []) => {
  const baseline = baselineValues.map(comparableServerValue).sort((left, right) => left.id - right.id);
  const current = currentValues.map(comparableServerValue).sort((left, right) => left.id - right.id);
  return JSON.stringify(baseline) === JSON.stringify(current)
    ? ''
    : 'Values changed on the server after this bulk editor was opened. Close and reopen it before saving so those changes are not overwritten.';
};

export const getFieldValueTemplateRows = (field, values = []) => [
  FIELD_VALUE_BULK_HEADERS,
  ...(values.length > 0
    ? values.map((value) => [
        value.id,
        field.id,
        getFieldValueMode(value) === 'range' ? 'Range' : 'Fixed',
        value.optionValue,
        value.minValue ?? '',
        value.maxValue ?? '',
        value.maxClicks ?? '',
        value.isDefaultValue ? 'TRUE' : 'FALSE',
      ])
    : [['', field.id, 'Fixed', '', '', '', '', 'FALSE']]),
];
